<template>
	<BaseStep title="Erzählen Sie uns von Ihrem Kind">
		<div class="space-y-6">
			<!-- Child Name -->
			<div class="mb-6">
				<!-- Child Gender -->
				<RadioCardGroup
					id="childGender"
					:model-value="modelValue.childGender"
					@update:model-value="updateGender"
					label="Geschlecht des Kindes"
					:options="genderOptions"
					layout="horizontal"
					helper-text="Für eine passende Ansprache in der Auswertung (optional)"
					data-testid="child-gender-input"
				/>
				<label for="childName" class="block mb-2 text-sm font-semibold text-gray-900">
					Name des Kindes *
				</label>
				<input
					id="childName"
					v-model="childName"
					type="text"
					required
					data-testid="child-name-input"
					:class="[
						'w-full px-4 py-3 border-2 rounded-lg transition-colors duration-200 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:outline-none bg-white text-gray-900 placeholder-gray-500',
						touchedFields.has('childName') && errors.childName
							? 'border-red-500 focus:border-red-500 focus:ring-red-500'
							: 'border-gray-300 hover:border-gray-400',
					]"
					placeholder="z.B. Anna"
					@blur="onBlur('childName')"
				/>
				<p
					v-if="touchedFields.has('childName') && errors.childName"
					class="flex items-center mt-2 text-sm text-red-600"
					data-testid="child-name-error"
				>
					<ErrorIcon class="flex-shrink-0 mr-1 w-4 h-4" />
					{{ errors.childName }}
				</p>
			</div>

			<!-- Child Age -->
			<div class="mb-6">
				<label for="childAge" class="block mb-2 text-sm font-semibold text-gray-900">
					Alter des Kindes *
				</label>
				<input
					id="childAge"
					v-model.number="childAge"
					type="number"
					min="3"
					max="18"
					required
					data-testid="child-age-input"
					:class="[
						'w-full px-4 py-3 border-2 rounded-lg transition-colors duration-200 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:outline-none bg-white text-gray-900 placeholder-gray-500',
						touchedFields.has('childAge') && errors.childAge
							? 'border-red-500 focus:border-red-500 focus:ring-red-500'
							: 'border-gray-300 hover:border-gray-400',
					]"
					placeholder="z.B. 10"
					@blur="onBlur('childAge')"
				/>
				<p
					v-if="touchedFields.has('childAge') && errors.childAge"
					class="flex items-center mt-2 text-sm text-red-600"
					data-testid="child-age-error"
				>
					<ErrorIcon class="flex-shrink-0 mr-1 w-4 h-4" />
					{{ errors.childAge }}
				</p>
			</div>

			<!-- Child School -->
			<div class="mb-6">
				<label for="childSchool" class="block mb-2 text-sm font-semibold text-gray-900">
					Schultyp/Bildungsform *
				</label>
				<select
					id="childSchool"
					v-model="childSchool"
					required
					data-testid="child-school-input"
					:class="[
						'w-full px-4 py-3 border-2 rounded-lg transition-colors duration-200 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:outline-none bg-white text-gray-900',
						touchedFields.has('childSchool') && errors.childSchool
							? 'border-red-500 focus:border-red-500 focus:ring-red-500'
							: 'border-gray-300 hover:border-gray-400',
					]"
					@blur="onBlur('childSchool')"
				>
					<option value="">Bitte wählen...</option>
					<option value="Kindergarten">Kindergarten</option>
					<option value="Grundschule">Grundschule</option>
					<option value="Hauptschule">Hauptschule</option>
					<option value="Mittelschule">Mittelschule</option>
					<option value="Wirtschaftsschule">Wirtschaftsschule</option>
					<option value="Realschule">Realschule</option>
					<option value="Gymnasium">Gymnasium</option>
					<option value="Gesamtschule">Gesamtschule</option>
					<option value="Förderschule">Förderschule</option>
					<option value="Andere">Andere</option>
				</select>
				<p
					v-if="touchedFields.has('childSchool') && errors.childSchool"
					class="flex items-center mt-2 text-sm text-red-600"
					data-testid="child-school-error"
				>
					<ErrorIcon class="flex-shrink-0 mr-1 w-4 h-4" />
					{{ errors.childSchool }}
				</p>
			</div>

			<!-- Child Interests -->
			<div class="mb-6">
				<label for="childInterests" class="block mb-2 text-sm font-semibold text-gray-900">
					Interessen & Hobbys (optional)
				</label>
				<textarea
					id="childInterests"
					v-model="childInterests"
					rows="3"
					data-testid="child-interests-input"
					class="px-4 py-3 w-full placeholder-gray-500 text-gray-900 bg-white rounded-lg border-2 border-gray-300 transition-colors duration-200 resize-none hover:border-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:outline-none"
					placeholder="Was macht Ihr Kind gerne in seiner Freizeit?"
				/>
			</div>

			<!-- Child Situation -->
			<div class="mb-6">
				<label for="childSituation" class="block mb-2 text-sm font-semibold text-gray-900">
					Lernsituation *
				</label>
				<textarea
					id="childSituation"
					v-model="childSituation"
					rows="4"
					required
					data-testid="child-situation-input"
					:class="[
						'w-full px-4 py-3 border-2 rounded-lg transition-colors duration-200 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:outline-none bg-white text-gray-900 placeholder-gray-500 resize-none',
						touchedFields.has('childSituation') && errors.childSituation
							? 'border-red-500 focus:border-red-500 focus:ring-red-500'
							: 'border-gray-300 hover:border-gray-400',
					]"
					placeholder="Beschreiben Sie die aktuelle Situation Ihres Kindes. Welche schulischen Herausforderungen oder ärztlichen Befunde gibt es zum Lernen? Arbeitet es lieber alleine als im Team?"
					@blur="onBlur('childSituation')"
				/>
				<p
					v-if="touchedFields.has('childSituation') && errors.childSituation"
					class="flex items-center mt-2 text-sm text-red-600"
					data-testid="child-situation-error"
				>
					<ErrorIcon class="flex-shrink-0 mr-1 w-4 h-4" />
					{{ errors.childSituation }}
				</p>
			</div>
		</div>
	</BaseStep>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import BaseStep from './BaseStep.vue';
import ErrorIcon from '@/components/icons/ErrorIcon.vue';
import RadioCardGroup from '@/components/common/RadioCardGroup.vue';

// Define props
interface ChildIntroData {
	childName: string;
	childAge: number | null;
	childSchool: string;
	childInterests: string;
	childSituation: string;
	childGender: string;
}

interface Props {
	modelValue: ChildIntroData;
	errors: Record<string, string>;
}

const props = defineProps<Props>();
const emit = defineEmits<{
	'update:modelValue': [value: ChildIntroData];
	validate: [];
	'field-touched': [field: string];
}>();

// Track touched fields
const touchedFields = ref<Set<string>>(new Set());

// Gender options with icons
const genderOptions = [
	{
		value: 'other',
		label: 'Neutral',
		icon: '👤',
		description: 'Keine Angabe',
	},
	{
		value: 'male',
		label: 'Junge',
		icon: '👦',
	},
	{
		value: 'female',
		label: 'Mädchen',
		icon: '👧',
	},
];

// Computed properties with getter/setter for proper v-model pattern
const childName = computed({
	get: () => props.modelValue.childName,
	set: (value) => {
		const updatedData = { ...props.modelValue, childName: value };
		emit('update:modelValue', updatedData);
	},
});

const childAge = computed({
	get: () => props.modelValue.childAge,
	set: (value) => {
		const updatedData = { ...props.modelValue, childAge: value };
		emit('update:modelValue', updatedData);
	},
});

const childSchool = computed({
	get: () => props.modelValue.childSchool,
	set: (value) => {
		const updatedData = { ...props.modelValue, childSchool: value };
		emit('update:modelValue', updatedData);
	},
});

const childInterests = computed({
	get: () => props.modelValue.childInterests,
	set: (value) => {
		const updatedData = { ...props.modelValue, childInterests: value };
		emit('update:modelValue', updatedData);
	},
});

const childSituation = computed({
	get: () => props.modelValue.childSituation,
	set: (value) => {
		const updatedData = { ...props.modelValue, childSituation: value };
		emit('update:modelValue', updatedData);
	},
});

// Methods
const onBlur = (field: string) => {
	touchedFields.value.add(field);
	emit('field-touched', field);
	emit('validate');
};

const updateGender = (value: string) => {
	const updatedData = { ...props.modelValue, childGender: value };
	emit('update:modelValue', updatedData);
};
</script>
